import React, { useRef, useEffect } from "react";
import { X } from "lucide-react";
import ChatMessageDisplay from "../../ChatMessageDisplay/ChatMessageDisplay";
import ThinkingIndicator from "./ThinkingIndicator";
import { ThinkingStatus } from "../types/drawerTypes";

interface MessagesAreaProps {
  messages: any[];
  config: any;
  currentHighlight: any;
  internalCADViewerAICallback: any;
  onSetInputValue: (value: string) => void;
  onRateMessage?: (message: any) => void;
  onFollowUp?: (replyContent: string) => void;
  thinkingText: string;
  thinkingStatuses: ThinkingStatus[];
  isThinkingVisible: boolean;
  isThinkingExpanded: boolean;
  isLoading: boolean;
  isSearching: boolean;
  onToggleThinkingExpanded: () => void;
  onToggleThinkingVisible: () => void;
  showSamplesOverlay?: boolean;
  onCloseSamples?: () => void;
}

interface SamplesListProps {
  examples: string[];
  onSelect: (example: string) => void;
}

const SamplesList: React.FC<SamplesListProps> = ({ examples, onSelect }) => (
  <>
    <div className="w-full max-w-3xl space-y-2">
      {examples.map((example, index) => (
        <button
          key={index}
          onClick={() => onSelect(example)}
          className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-orange-500 hover:shadow-md transition-all duration-200 group"
        >
          <p className="text-xs text-gray-700 group-hover:text-gray-900 leading-snug line-clamp-2">
            {example}
          </p>
        </button>
      ))}
    </div>
    <div className="mt-8 text-center text-xs text-gray-500">
      Click any example to populate the input field, then press Send
    </div>
  </>
);

const MessagesArea: React.FC<MessagesAreaProps> = ({
  messages,
  config,
  currentHighlight,
  internalCADViewerAICallback,
  onSetInputValue,
  onRateMessage,
  onFollowUp,
  thinkingText,
  thinkingStatuses,
  isThinkingVisible,
  isThinkingExpanded,
  isLoading,
  isSearching,
  onToggleThinkingExpanded,
  onToggleThinkingVisible,
  showSamplesOverlay = false,
  onCloseSamples,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    // Explicitly scroll the container, DO NOT use scrollIntoView as it can shift the parent body
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [
    messages.length,
    messages.length > 0 ? messages[messages.length - 1].content : null,
    thinkingText,
    thinkingStatuses.length
  ]);

  const baseExamples: string[] = config.aiBaseQueries ?? [];
  const userQueries = messages
    .filter(msg => msg.role === "user")
    .map(msg => msg.content)
    .filter(content => typeof content === "string" && content.trim() !== "");
  
  // Combine recent user queries (newest first) with base examples, removing duplicates
  const examples = Array.from(new Set([...userQueries].reverse().concat(baseExamples)));

  const handleSampleSelect = (example: string) => {
    onSetInputValue(example);
  };

  return (
    <div className="relative z-10 flex-1 min-h-0 flex flex-col mr-2 bg-gray-50">
      <div 
        ref={scrollContainerRef} 
        className={`flex-1 min-h-0 p-4 space-y-6 ${
          showSamplesOverlay && messages.length > 0 ? "overflow-hidden" : "overflow-y-auto"
        }`}
      >
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full px-6">
          <SamplesList examples={examples} onSelect={handleSampleSelect} />
        </div>
      ) : (
        messages.map((msg: any) => (
          <ChatMessageDisplay
            key={msg.uuid}
            message={msg}
            currentHighlight={currentHighlight}
            internalCADViewerAICallback={internalCADViewerAICallback}
            onSetInputValue={onSetInputValue}
            onRateMessage={onRateMessage}
            onFollowUp={onFollowUp}
          />
        ))
      )}

      <ThinkingIndicator
        thinkingText={thinkingText}
        thinkingStatuses={thinkingStatuses}
        isLoading={isLoading}
        isThinkingExpanded={isThinkingExpanded}
        isThinkingVisible={isThinkingVisible}
        onToggleExpanded={onToggleThinkingExpanded}
        onToggleVisible={onToggleThinkingVisible}
      />

      {(isLoading || isSearching) &&
        !thinkingText &&
        thinkingStatuses.length === 0 && (
          <div className="flex justify-center items-center py-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
            <span className="ml-2 text-sm text-gray-500">
              {isLoading
                ? "AI is thinking..."
                : isSearching
                ? "AI is searching..."
                : ""}
            </span>
          </div>
        )}

      {/* spacer for bottom */}
      <div className="h-2" />
      </div>

      {showSamplesOverlay && messages.length > 0 && (
        <div className="absolute inset-0 z-20 flex flex-col bg-gray-50/95 backdrop-blur-sm overflow-y-auto !m-0">
          <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white/80 sticky top-0">
            <h3 className="text-sm font-semibold text-gray-800">Sample questions</h3>
            {onCloseSamples && (
              <button
                onClick={onCloseSamples}
                className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close sample questions"
                title="Close"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <div className="flex flex-col items-center justify-center flex-1 px-6 py-6">
            <SamplesList examples={examples} onSelect={handleSampleSelect} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesArea;
